import type { SagaIterator } from 'redux-saga';
import { all, takeEvery, takeLatest } from 'redux-saga/effects';
import * as dashboardSagas from './modules/dashboard/dashboardSagas';
import { DashboardActionTypes as dashboardTypes } from './modules/dashboard/dashboardTypes';
import * as emailSagas from './modules/email/emailSagas';
import { EmailActionTypes as emailTypes } from './modules/email/emailTypes';
import * as emailOnboardingSagas from './modules/emailOnboarding/emailOnboardingSagas';
import { EmailOnboardingActionTypes as emailOnboardingTypes } from './modules/emailOnboarding/emailOnboardingTypes';
import * as reportSagas from './modules/report/reportSagas';
import { ReportActionTypes as reportTypes } from './modules/report/reportTypes';
import * as searchSagas from './modules/search/searchSagas';
import { SearchActionTypes as searchTypes } from './modules/search/searchTypes';
import * as userSagas from './modules/user/userSagas';
import { UserActionTypes as userTypes } from './modules/user/userTypes';
import * as freeTrialSagas from './modules/freeTrial/freeTrialSagas';
import { FreeTrialActionTypes as freeTrialTypes } from './modules/freeTrial/freeTrialTypes';
import * as demandReportsSagas from './modules/demandReports/demandReportsSagas';
import { DemandReportsActionTypes as demandReportsTypes } from './modules/demandReports/demandReportsTypes';
import * as companySagas from './modules/company/companySagas';
import { CompanyActionTypes as companyActionTypes } from './modules/company/companyTypes';

export default function* root(): SagaIterator {
	yield all([
		takeLatest(
			freeTrialTypes.STRIPE_SUCCESS_SUBSCRIPTION,
			freeTrialSagas.stripeSuccessSubscriptionSaga
		),
		takeLatest(
			freeTrialTypes.HUBSPOT_PRETRIAL,
			freeTrialSagas.hubspotPretrialSaga
		),
		takeLatest(
			freeTrialTypes.HUBSPOT_PRETRIAL_FAILURE,
			freeTrialSagas.hubspotPretrialSaga
		),
		takeLatest(userTypes.USER_LOGIN_REQUEST, userSagas.loginUserSaga),
		takeLatest(
			userTypes.USER_RESET_PASSWORD_REQUEST,
			userSagas.userResetPasswordSaga
		),
		takeLatest(
			userTypes.USER_ADMIN_RESET_PASSWORD_REQUEST,
			userSagas.adminResetPasswordSaga
		),
		takeLatest(userTypes.USER_GET_USER_REQUEST, userSagas.getUserSaga),
		takeLatest(
			userTypes.USER_GET_USER_BY_EMAIL_REQUEST,
			userSagas.getUserByEmailSaga
		),
		takeLatest(userTypes.USER_GET_USERS_REQUEST, userSagas.getUsersSaga),
		takeLatest(
			userTypes.USER_ADD_EMAIL_TOKEN,
			userSagas.userAddEmailTokenSaga
		),
		takeLatest(
			dashboardTypes.DASHBOARD_GET_BUSINESS_REQUEST,
			dashboardSagas.getBusinessSaga
		),
		takeLatest(
			dashboardTypes.DASHBOARD_GET_BUSINESSES_REQUEST,
			dashboardSagas.getBusinessesSaga
		),
		takeLatest(
			dashboardTypes.DASHBOARD_GET_SELECT_BUSINESS,
			dashboardSagas.getSelectBusinessSaga
		),
		takeLatest(
			dashboardTypes.DASHBOARD_SET_BUSINESS_REMINDER,
			dashboardSagas.setBusinessReminder
		),
		takeLatest(
			dashboardTypes.DASHBOARD_DELETE_BUSINESS_REMINDER,
			dashboardSagas.deleteBusinessReminder
		),
		takeLatest(
			dashboardTypes.DASHBOARD_UPDATE_BUSINESS_NOTES_REQUEST,
			dashboardSagas.updateBusinessNotesSaga
		),
		takeLatest(
			dashboardTypes.DASHBOARD_GET_LISTS_REQUEST,
			dashboardSagas.getListsSaga
		),
		takeLatest(
			dashboardTypes.DASHBOARD_GET_SUBORDINATE_LISTS_REQUEST,
			dashboardSagas.getSubordinateListsSaga
		),
		takeLatest(
			dashboardTypes.DASHBOARD_ADD_LIST_REQUEST,
			dashboardSagas.addListSaga
		),
		takeLatest(
			dashboardTypes.DASHBOARD_EDIT_LIST_REQUEST,
			dashboardSagas.editListSaga
		),
		takeLatest(
			emailTypes.EMAIL_SEND_EMAILS_REQUEST,
			emailSagas.sendEmailsSaga
		),
		takeLatest(
			userTypes.USER_UPDATE_EMAILS_LIMIT_REQUEST,
			userSagas.updateUserEmailsLimitSaga
		),
		takeLatest(
			userTypes.USER_UPDATE_USER_ADMIN_REQUEST,
			userSagas.getUpdateUserAdminSaga
		),
		takeLatest(
			userTypes.USER_UPDATE_SUBSCRIPTION,
			userSagas.updateUserSubscription
		),
		takeEvery(
			dashboardTypes.DASHBOARD_ADD_BUSINESS_TO_LIST_REQUEST,
			dashboardSagas.addBusinessToListSaga
		),
		takeEvery(
			dashboardTypes.DASHBOARD_REMOVE_BUSINESS_FROM_LIST_REQUEST,
			dashboardSagas.removeBusinessFromListSaga
		),
		takeLatest(
			dashboardTypes.DASHBOARD_REMOVE_LIST_REQUEST,
			dashboardSagas.removeListSaga
		),
		takeLatest(
			dashboardTypes.DASHBOARD_REMOVE_BATCH_LISTS_REQUEST,
			dashboardSagas.removeListsBatchSaga
		),
		takeLatest(
			dashboardTypes.DASHBOARD_EXPORT_BUSINESSES_REQUEST,
			dashboardSagas.exportBusinessesSaga
		),
		takeLatest(
			dashboardTypes.DASHBOARD_ADD_BUSINESS_DATA,
			dashboardSagas.addBusinessDataSaga
		),
		takeLatest(
			dashboardTypes.DASHBOARD_DELETE_BUSINESSES_DATA,
			dashboardSagas.deleteBusinessDataSaga
		),
		takeLatest(
			dashboardTypes.DASHBOARD_GET_MAPBOX_KEY,
			dashboardSagas.getMapBoxKeySaga
		),
		takeLatest(
			dashboardTypes.DASHBOARD_GET_COMMUNICATION_CENTER_BUSINESSES,
			dashboardSagas.getCommunicationCenterBusinesses
		),
		takeLatest(
			userTypes.USER_UPDATE_EMAILS_LIMIT_REQUEST,
			userSagas.updateUserEmailsLimitSaga
		),
		takeLatest(reportTypes.REPORT_ADD_REQUEST, reportSagas.addReportSaga),
		takeLatest(
			searchTypes.SEARCH_GET_SEARCHES_REQUEST,
			searchSagas.getSearchesSaga
		),
		takeLatest(
			searchTypes.SEARCH_ADD_SEARCH_REQUEST,
			searchSagas.addSearchSaga
		),
		takeLatest(
			searchTypes.SEARCH_EDIT_SEARCH_REQUEST,
			searchSagas.editSearchSaga
		),
		takeLatest(
			searchTypes.SEARCH_REMOVE_SEARCH_REQUEST,
			searchSagas.removeSearchSaga
		),
		takeLatest(
			reportTypes.REPORT_GET_REPORTS_REQUEST,
			reportSagas.getReportsSaga
		),
		takeLatest(
			emailTypes.EMAIL_GET_EMAIL_TEMPLATES_REQUEST,
			emailSagas.getEmailTemplatesSaga
		),
		takeLatest(
			emailTypes.EMAIL_CREATE_EMAIL_TEMPLATE_REQUEST,
			emailSagas.createEmailTemplateSaga
		),
		takeLatest(
			emailTypes.EMAIL_UPDATE_EMAIL_TEMPLATE_REQUEST,
			emailSagas.updateEmailTemplateSaga
		),
		takeLatest(
			emailTypes.EMAIL_DELETE_EMAIL_TEMPLATE_REQUEST,
			emailSagas.deleteEmailTemplateSaga
		),
		takeLatest(
			emailTypes.EMAIL_DUPLICATE_EMAIL_TEMPLATE_REQUEST,
			emailSagas.duplicateEmailTemplateSaga
		),
		takeLatest(
			emailTypes.EMAIL_GET_EMAIL_THREAD,
			emailSagas.emailGetEmailThread
		),
		takeLatest(
			emailTypes.EMAIL_GET_EMAIL_CONTACTED_STATUS,
			emailSagas.emailGetEmailContactedStatus
		),
		takeLatest(
			emailOnboardingTypes.EMAIL_ONBOARDING_NYLAS_SIGNUP_REQUEST,
			emailOnboardingSagas.emailOnboardingRequest
		),
		takeLatest(
			emailOnboardingTypes.EMAIL_ONBOARDING_NYLAS_CALENDAR_SIGNUP_REQUEST,
			emailOnboardingSagas.emailCalendarOnboardingRequest
		),
		takeLatest(
			userTypes.USER_ADD_NEW_USER_REQUEST,
			userSagas.userAddNewUserRequest
		),
		takeLatest(
			userTypes.USER_ADD_HOSTED_EMAIL,
			userSagas.userAddHostedEmail
		),
		takeLatest(
			userTypes.USER_DELETE_USER_REQUEST,
			userSagas.userDeleteUserRequest
		),
		takeLatest(
			userTypes.USER_ADD_NEW_BCC_REQUEST,
			userSagas.userAddNewBccRequest
		),
		takeLatest(
			userTypes.USER_DELETE_BCC_REQUEST,
			userSagas.userDeleteBccRequest
		),
		takeLatest(
			userTypes.USER_CREATE_SCHEDULE_REQUEST,
			userSagas.userCreateScheduleRequest
		),
		takeLatest(userTypes.USER_LOGOUT_REQUEST, userSagas.userLogoutRequest),
		takeLatest(
			userTypes.USER_GET_NEW_COMMUNICATIONS,
			userSagas.getUserNewCommunications
		),
		takeLatest(
			userTypes.USER_GET_NOTIFICATIONS,
			userSagas.getUserNotifications
		),
		takeLatest(
			userTypes.USER_POST_NOTIFICATIONS_READ,
			userSagas.postUserNotificationsRead
		),
		takeLatest(
			dashboardTypes.DASHBOARD_GET_ACTIVITIES,
			dashboardSagas.getActivities
		),
		takeLatest(
			dashboardTypes.DASHBOARD_CREATE_ACTIVITY_NOTE,
			dashboardSagas.createActivityNote
		),
		takeLatest(
			dashboardTypes.DASHBOARD_GET_ACTIVITY_EMAIL_OUTBOUND,
			dashboardSagas.getActivityEmailOutbound
		),
		takeLatest(
			dashboardTypes.DASHBOARD_GET_ACTIVITY_EMAIL_INBOUND,
			dashboardSagas.getActivityEmailInbound
		),
		takeLatest(
			demandReportsTypes.DEMAND_REPORT_GET_REPORT,
			demandReportsSagas.getDemandReportSaga
		),
		takeLatest(
			demandReportsTypes.DEMAND_REPORT_GET_REPORTS,
			demandReportsSagas.getDemandReportsSaga
		),
		takeLatest(
			emailTypes.EMAIL_GET_EMAIL_STATUSES,
			emailSagas.emailGetEmailStatuses
		),
		takeLatest(
			emailTypes.EMAIL_GET_SCHEDULED_EMAIL_LIMITS,
			emailSagas.getScheduledEmailLimits
		),
		takeLatest(
			emailTypes.EMAIL_CANCEL_SCHEDULED_EMAIL,
			emailSagas.cancelScheduledEmail
		),
		takeLatest(
			emailTypes.EMAIL_GET_SCHEDULED_EMAILS,
			emailSagas.getScheduledEmails
		),
		takeLatest(
			emailTypes.EMAIL_CANCEL_SCHEDULED_BATCH_EMAILS_ADMIN,
			emailSagas.cancelScheduledBatchEmailsAdmin
		),
		takeLatest(
			emailTypes.EMAIL_CANCEL_SCHEDULED_BATCH_EMAILS_USER,
			emailSagas.cancelScheduledBatchEmailsUser
		),
		takeLatest(
			emailTypes.EMAIL_GET_SCHEDULED_EMAILS_COUNT_BY_DATE,
			emailSagas.getScheduledEmailsCountByDate
		),
		takeLatest(
			emailTypes.EMAIL_GET_SCHEDULED_EMAILS_COUNT_BY_TIME_PERIOD,
			emailSagas.getScheduledEmailsCountByTimePeriod
		),
		takeLatest(
			dashboardTypes.DASHBOARD_GET_GOOGLE_PLACES,
			dashboardSagas.getGooglePlaces
		),
		takeLatest(
			dashboardTypes.DASHBOARD_REQUEST_NEW_LOCATION,
			dashboardSagas.requestNewLocation
		),
		takeLatest(
			dashboardTypes.DASHBOARD_REQUEST_ADD_LOCATION_FEATURE,
			dashboardSagas.requestAddLocationFeature
		),
		takeLatest(
			userTypes.USER_ADMIN_ACCESS_ACCOUNT_REQUEST,
			userSagas.adminAccessAccountSaga
		),
		takeLatest(
			emailTypes.EMAIL_GET_EMAIL_TEMPLATE_SCORES,
			emailSagas.getEmailTemplateScores
		),
		takeLatest(userTypes.USER_CHECK_USER_EXISTS, userSagas.checkUserExists),
		takeLatest(
			userTypes.USER_SIGN_UP_FACEBOOK_FREEMIUM,
			userSagas.signUpFacebookFreemium
		),
		takeLatest(
			userTypes.USER_UPDATE_EMAIL_SENDING_METHOD,
			userSagas.updateEmailSendingMethod
		),
		takeLatest(
			userTypes.USER_UPDATE_USER_ACTIVE_STATUS,
			userSagas.updateUserActiveStatus
		),
		takeLatest(
			userTypes.USER_VERIFY_SIGNUP_TOKEN,
			userSagas.verifySignupEmail
		),
		takeLatest(emailTypes.EMAIL_SEND_ALL, emailSagas.sendAll),
		takeLatest(emailTypes.EMAIL_GET_AI_SNIPPETS, emailSagas.getAISnippets),
		takeLatest(
			emailTypes.EMAIL_REGENERATE_AI_SNIPPET,
			emailSagas.regenerateAISnippet
		),
		takeLatest(emailTypes.EMAIL_GET_AI_CREDITS, emailSagas.getAICredits),
		takeLatest(
			dashboardTypes.DASHBOARD_GET_ALL_BUSINESSES,
			dashboardSagas.getAllBusinessesSaga
		),
		takeLatest(
			emailTypes.EMAIL_SEND_CUSTOMER_REPORT_BAD_DATA,
			emailSagas.sendCustomerReportBadData
		),
		takeLatest(
			emailTypes.EMAIL_GET_MONTHLY_EMAIL_LIMIT,
			emailSagas.getMonthlyEmailLimit
		),
		takeLatest(
			userTypes.USER_GENERATE_OTP_SECRET,
			userSagas.generateOtpSecret
		),
		takeLatest(userTypes.USER_ENABLE_OTP, userSagas.enableOtp),
		takeLatest(userTypes.USER_DEACTIVATE_OTP, userSagas.deactivateOtp),
		takeLatest(userTypes.USER_VERIFY_OTP, userSagas.verifyOtp),
		takeLatest(userTypes.USER_RESET_OTP, userSagas.resetOtp),
		takeLatest(
			companyActionTypes.USER_GET_COMPANIES_REQUEST,
			companySagas.getCompaniesSaga
		),
		takeLatest(
			companyActionTypes.CREATE_COMPANY_REQUEST,
			companySagas.createCompanySaga
		),
		takeLatest(
			companyActionTypes.UPDATE_COMPANY_REQUEST,
			companySagas.updateCompanySaga
		),
		takeLatest(
			userTypes.USER_ADMIN_CREATE_ACCOUNTS_REQUEST,
			userSagas.createUserAccountsSaga
		),
		takeLatest(
			companyActionTypes.SEARCH_HUBSPOT_COMPANY_REQUEST,
			companySagas.searchHubspotCompanySaga
		),
		takeLatest(
			companyActionTypes.GET_COMPANY_HS_ID_REQUEST,
			companySagas.getCompanyHubspotId
		),
		takeLatest(
			companyActionTypes.UPDATE_COMPANY_HS_ID_REQUEST,
			companySagas.updateCompanyHsId
		)
	]);
}
